import { useRef, type KeyboardEvent } from 'react';

import { TogglePill } from '../../dashboards/primitives';
import type { PrivateEventLocationMode } from '../private-event-form-state';

import type { LocationFormApi } from './when-what-form-api';

const LOCATION_MODES: { id: PrivateEventLocationMode; label: string }[] = [
  { id: 'venue', label: 'Partner venue' },
  { id: 'client-site', label: 'Client address' },
  { id: 'tbd', label: 'To be determined' },
];

const LABEL_ID = 'private-event-location-label';

/** The index an arrow or Home/End key moves to, or null for any other key. */
function targetIndex(key: string, current: number): number | null {
  const last = LOCATION_MODES.length - 1;
  if (key === 'ArrowRight' || key === 'ArrowDown') return current === last ? 0 : current + 1;
  if (key === 'ArrowLeft' || key === 'ArrowUp') return current === 0 ? last : current - 1;
  if (key === 'Home') return 0;
  return key === 'End' ? last : null;
}

/** Which kind of place the event is at. Exactly one is ever active, so it is a
 *  radio group: one tab stop, moved with the arrow keys, Home and End. */
export default function LocationModeToggle({ form }: { form: LocationFormApi }) {
  const group = useRef<HTMLDivElement>(null);
  const current = LOCATION_MODES.findIndex((m) => m.id === form.fields.locationMode);

  const onKeyDown = (e: KeyboardEvent) => {
    const next = targetIndex(e.key, current);
    if (next === null) return;
    e.preventDefault();
    form.setLocationMode(LOCATION_MODES[next].id);
    group.current?.querySelectorAll<HTMLElement>('[role="radio"]')[next]?.focus();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <span id={LABEL_ID} style={{ fontSize: 11, fontWeight: 700, color: 'var(--nb-ink-2)' }}>
        Location
      </span>
      <div
        ref={group}
        role="radiogroup"
        aria-labelledby={LABEL_ID}
        onKeyDown={onKeyDown}
        style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}
      >
        {LOCATION_MODES.map((m, i) => (
          <TogglePill
            key={m.id}
            role="radio"
            tabIndex={i === current ? 0 : -1}
            selected={i === current}
            onClick={() => form.setLocationMode(m.id)}
          >
            {m.label}
          </TogglePill>
        ))}
      </div>
    </div>
  );
}
